import { Box, Button, Typography } from '@mui/material';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getTranslatedValue } from 'src/common/utils/format-utils';
import { References } from 'src/components/references';
import { useDialog, useResources } from '../../../hooks';
import { deleteRegion, getRegions } from '../../../thunks/region-thunks';
import { DataTable, DataTableFilter } from '../../data-table';
import { DataTableFooter } from '../../data-table/data-table-footer';
import { ConfirmDialog } from '../../dialogs';
import { PlusIcon } from '../../icons';
import { OrganizationsTableActions } from '../../organizations';
import { RegionsEditDialog } from '../regions-edit-dialog';

export const RegionsList: ComponentType = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [openRegionsEdit, closeRegionsEdit] = useDialog(RegionsEditDialog);
  const [openConfirm, closeConfirm] = useDialog(ConfirmDialog);
  const {
    list: { hits, isLoading, total },
    query,
    handleQueryUpdate,
    handleFetchFirstPage,
    handleFetchCurrentPage,
  } = useResources({
    entity: 'region',
    onGetAll: getRegions,
  });

  const handleCreate = () => {
    openRegionsEdit({
      title: t('regions.add', 'Добавить регион'),
      onSubmit: () => {
        closeRegionsEdit();
        handleFetchFirstPage();
      },
    });
  };

  const handleEdit = (id: string | number) => {
    openRegionsEdit({
      title: t('regions.edit', 'Редактировать регион'),
      id,
      onSubmit: () => {
        closeRegionsEdit();
        handleFetchCurrentPage();
      },
    });
  };

  const handleDelete = (id: string | number) => {
    openConfirm({
      size: 'small',
      resolve: () => {
        closeConfirm();
        deleteRegion(id)(dispatch).then(() => {
          handleFetchFirstPage();
        });
      },
    });
  };

  const data = {
    hits,
    isLoading,
    columns: [
      {
        width: 60,
        label: '№',
        labelKey: '',
        renderItem: (item: Record<string, any>) => (
          <Typography variant="h5" color="primary">
            {item.id}
          </Typography>
        ),
      },
      {
        width: 'auto',
        label: 'Регион',
        labelKey: 'region',
        renderItem: (item: Record<string, any>) => getTranslatedValue(item.title, i18n.language),
      },
      {
        width: 100,
        label: 'Код',
        labelKey: 'code',
        renderItem: (item: Record<string, any>) => item.code,
      },
      {
        width: 96,
        label: '',
        labelKey: '',
        renderItem: (item: Record<string, any>) => (
          <OrganizationsTableActions item={item} onEdit={handleEdit} onDelete={handleDelete} />
        ),
      },
    ],
  };

  return (
    <References>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <DataTableFilter {...{ value: query.search, onUpdate: handleQueryUpdate }} />
        <Button
          onClick={handleCreate}
          variant="contained"
          color="secondary"
          startIcon={<PlusIcon />}
          size="small"
        >
          {t('regions.add', 'Добавить направление')}
        </Button>
      </Box>
      <DataTable {...data} />
      <DataTableFooter {...{ total, query, onUpdate: handleQueryUpdate }} />
    </References>
  );
};
